/**
 * PropertyPicker — searchable property link for the task and report
 * forms (D4b).
 *
 * Seventh thin wrapper over the shared ``AsyncCombobox`` (C0). It owns
 * only the property vocabulary and delegates debounce, paging,
 * stale-response protection, keyboard handling and theming to the base.
 *
 * Rendered shape:
 *   Wohnhaus Dufourstrasse
 *   Dufourstrasse 12, 8008 Zürich
 *
 * Server contract:
 *   GET /api/admin/properties/picker-options?q=&limit=&offset=  (D4a2)
 *
 * **Hydration comes from the caller, not from a second request.** The
 * older pickers (Tenancy, Unit) fetch the current selection by id,
 * because when they were built the row's own response carried no name.
 * D4a3 changed that: a task or report now ships
 * ``property_display_name`` beside ``property_id``, so the form can hand
 * the label straight in. One fewer round-trip per open, and — decisive
 * for the sibling RoomPicker — it works for an entity that has no
 * detail-by-id route at all.
 *
 * The payload it feeds back is a bare ``property_id`` string; the empty
 * string is the unlink path, which the form turns into an explicit null.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import { fetchAdminPropertyPickerOptions } from "../../../../api/adminData";

const PAGE_SIZE = 20;

/**
 * The name a person recognises the building by. The server already
 * composes this (title → assembled address → "Liegenschaft"), so this
 * only unwraps it and never invents a fallback of its own — the two
 * would drift. Never a UUID.
 * Exported for tests.
 */
export function propertyLabel(property) {
  const label = (property?.label || "").trim();
  if (label) return label;
  const title = (property?.title || "").trim();
  if (title) return title;
  return "Liegenschaft ohne Bezeichnung";
}

/**
 * "Dufourstrasse 12, 8008 Zürich" — the postal address as a secondary
 * line. Missing segments are dropped rather than rendered as empty
 * separators, and the whole line is null when nothing is known.
 * Exported for tests.
 */
export function propertyAddress(property) {
  const street = [
    (property?.street || "").trim(),
    (property?.house_number || "").trim(),
  ]
    .filter(Boolean)
    .join(" ");
  const place = [
    (property?.zip_code || "").trim(),
    (property?.city || "").trim(),
  ]
    .filter(Boolean)
    .join(" ");
  return [street, place].filter(Boolean).join(", ") || null;
}

export default function PropertyPicker({
  value,
  onChange,
  /** ``{ id, label }`` for the current link, built by the form from the
   * row's own ``property_id`` / ``property_display_name``. Without it an
   * edit form would show "…" until the user opens the list. */
  selectedOption = null,
  disabled = false,
}) {
  // Locally selected row wins once the user picks one; otherwise the
  // caller's hydrated option. Keyed by id so a value changed from
  // OUTSIDE (a form reset, a different task loaded into the same modal)
  // discards a stale local selection instead of showing the old name.
  const [picked, setPicked] = React.useState(null);

  const selected =
    picked && String(picked.id) === String(value || "")
      ? picked
      : selectedOption && String(selectedOption.id) === String(value || "")
        ? selectedOption
        : null;

  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminPropertyPickerOptions({
        q: query || undefined,
        limit,
        offset,
      }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setPicked(item);
      // The full row goes back too, matching RoomPicker: the caller
      // keeps a hydrated option and does not have to re-derive the
      // label from an id it just handed over.
      onChange(id, item);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Liegenschaft (optional)"
      placeholder="— keine —"
      searchPlaceholder="Liegenschaft nach Name, Strasse, PLZ oder Ort suchen …"
      loadingText="Liegenschaften werden geladen …"
      emptyText="Keine passenden Liegenschaften gefunden."
      errorText="Liegenschaften konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(p) => p.id}
      getItemLabel={(p) => propertyLabel(p)}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(property) => (
        <span className="block min-w-0">
          <span
            title={propertyLabel(property)}
            className={
              "block truncate text-[13px] " +
              (property?.is_archived
                ? "font-normal text-muted-foreground"
                : "font-medium text-foreground")
            }
          >
            {propertyLabel(property)}
          </span>
          {propertyAddress(property) ? (
            <span
              title={propertyAddress(property)}
              className="block truncate text-[11px] text-muted-foreground"
            >
              {propertyAddress(property)}
            </span>
          ) : null}
        </span>
      )}
    />
  );
}
